/**
 * Thin browser-side bridge to the Solem Chrome extension.
 *
 * Why this file exists:
 *   - The web app talks to the extension via chrome.runtime.sendMessage with
 *     an explicit extension ID (set in env). That ID can be ONE of several
 *     (prod + 1–2 dev variants for sandboxes), so all queries fan out across
 *     IDs and aggregate.
 *   - Two callers need this surface: the onboarding wizard's PING liveness
 *     loop (Step 1 of OnboardingStart) and /settings#shortcuts (this PR).
 *   - Multi-ID aggregation has subtle precedence rules (see classifyResults
 *     below) that we want in exactly one place — not duplicated.
 *
 * Failure-mode taxonomy across the listener:
 *   - lastError "Receiving end does not exist" → that extension ID is NOT
 *     installed in this browser (no listener registered at all).
 *   - undefined response, no lastError → extension IS installed, but its
 *     listener didn't match this message type. For PING this should never
 *     happen on v0.5.3+; for GET_SHORTCUTS it means the user is on an
 *     extension version older than v0.5.6 ("old version" path).
 *   - well-shaped response → success.
 *
 * Stale-request guard: callers are expected to wrap the returned promises
 * in their own aliveness check (e.g. React useRef gen-counter); we don't
 * bake one in here because the right cancellation strategy depends on the
 * caller (focus-refetch vs. interval poll vs. fire-and-forget).
 */

import type { ShortcutInfo } from '@/types/extension';

// ────────────────────────────────────────────────────────────────────
// Discovery
// ────────────────────────────────────────────────────────────────────

export function getExtensionIds(): string[] {
  const csv = import.meta.env.VITE_CHROME_EXTENSION_IDS;
  if (typeof csv === 'string' && csv.length > 0) {
    return csv.split(',').map((s: string) => s.trim()).filter(Boolean);
  }
  return [
    import.meta.env.VITE_CHROME_EXTENSION_ID_PRODUCTION,
    import.meta.env.VITE_CHROME_EXTENSION_ID_DEV14,
    import.meta.env.VITE_CHROME_EXTENSION_ID_DEV16,
  ].filter((s: unknown): s is string => typeof s === 'string' && s.length > 0);
}

// ────────────────────────────────────────────────────────────────────
// Low-level sendMessage wrapper
// ────────────────────────────────────────────────────────────────────

type ChromeRuntime = {
  sendMessage?: (id: string, msg: unknown, cb: (resp: unknown) => void) => void;
  lastError?: { message?: string };
};

function getChromeRuntime(): ChromeRuntime | null {
  const w = window as unknown as { chrome?: { runtime?: ChromeRuntime } };
  return w.chrome?.runtime ?? null;
}

export type SendResult<T> =
  | { kind: 'ok'; data: T }
  | { kind: 'no_extension' }       // lastError "Receiving end does not exist"
  | { kind: 'no_handler' }         // undefined response, no lastError (old version, unknown message type)
  | { kind: 'error'; message: string }; // some other transport error or timeout

const DEFAULT_TIMEOUT_MS = 1500;

/**
 * Send a message to one extension ID and classify the outcome. The shape
 * of `T` is whatever the extension's listener calls sendResponse() with.
 *
 * IMPORTANT: We classify, we don't interpret. The caller decides what an
 * `ok` response means semantically (e.g. for GET_SHORTCUTS, the caller
 * additionally checks for the presence of the `shortcuts` array).
 */
export function sendToExtension<T = unknown>(
  id: string,
  message: unknown,
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
): Promise<SendResult<T>> {
  return new Promise((resolve) => {
    const runtime = getChromeRuntime();
    if (!runtime?.sendMessage) {
      // Not a Chrome-extension-capable browser at all.
      resolve({ kind: 'no_extension' });
      return;
    }
    let settled = false;
    const settleOnce = (r: SendResult<T>) => {
      if (settled) return;
      settled = true;
      resolve(r);
    };
    const timer = setTimeout(
      () => settleOnce({ kind: 'error', message: 'timeout' }),
      timeoutMs,
    );
    try {
      runtime.sendMessage(id, message, (resp: unknown) => {
        clearTimeout(timer);
        const err = runtime.lastError;
        if (err) {
          if (err.message?.includes('Receiving end does not exist')) {
            settleOnce({ kind: 'no_extension' });
          } else {
            settleOnce({ kind: 'error', message: err.message ?? 'unknown' });
          }
          return;
        }
        if (resp === undefined || resp === null) {
          // Listener was reached (no lastError) but didn't call sendResponse
          // for this message type → extension exists but doesn't handle it.
          // For v0.5.5 this is the GET_SHORTCUTS / OPEN_SHORTCUTS_PAGE path.
          settleOnce({ kind: 'no_handler' });
          return;
        }
        settleOnce({ kind: 'ok', data: resp as T });
      });
    } catch (e) {
      clearTimeout(timer);
      settleOnce({ kind: 'error', message: e instanceof Error ? e.message : String(e) });
    }
  });
}

// ────────────────────────────────────────────────────────────────────
// PING — installed/authed probe
// ────────────────────────────────────────────────────────────────────

export interface PingResult {
  present: boolean;
  authed: boolean;
}

const EMPTY_PING: PingResult = { present: false, authed: false };

export async function pingExtension(id: string, timeoutMs?: number): Promise<PingResult> {
  const r = await sendToExtension<{ pong?: boolean; authed?: boolean }>(
    id,
    { type: 'PING' },
    timeoutMs,
  );
  if (r.kind === 'ok') {
    return { present: !!r.data.pong, authed: !!r.data.authed };
  }
  if (r.kind === 'no_handler') {
    // Extension reached but didn't respond to PING. Treat as installed-but-
    // ancient (≤0.5.2). Not authed because we have no signal.
    return { present: true, authed: false };
  }
  // no_extension | error → treat as absent. Onboarding's interval will retry.
  return EMPTY_PING;
}

export async function pingAnyExtension(): Promise<PingResult> {
  const ids = getExtensionIds();
  if (ids.length === 0) return EMPTY_PING;
  const results = await Promise.all(ids.map((id) => pingExtension(id)));
  return {
    present: results.some((r) => r.present),
    authed: results.some((r) => r.authed),
  };
}

// ────────────────────────────────────────────────────────────────────
// Shortcuts probe (v0.5.6+)
// ────────────────────────────────────────────────────────────────────

export type ShortcutsProbeResult =
  | { kind: 'supported'; shortcuts: ShortcutInfo[] }
  | { kind: 'installed_old' }          // PING works, GET_SHORTCUTS doesn't
  | { kind: 'not_installed' }
  | { kind: 'error'; message: string };

interface GetShortcutsResponse {
  shortcuts?: ShortcutInfo[];
  error?: string;
}

/**
 * Probe a single extension ID for shortcut state. Two parallel sends:
 *
 *   PING            → tells us if the extension exists at all
 *   GET_SHORTCUTS   → tells us if the extension supports v0.5.6+ surface
 *
 * Running them in parallel rather than sequentially halves the worst-case
 * latency when the user has multiple browsers/profiles open. They go to
 * the same listener but Chrome serializes message handling per extension
 * instance, which is fine for our purposes.
 */
async function probeShortcutsForId(id: string): Promise<ShortcutsProbeResult> {
  const [pingResult, shortcutsResult] = await Promise.all([
    sendToExtension<{ pong?: boolean }>(id, { type: 'PING' }),
    sendToExtension<GetShortcutsResponse>(id, { type: 'GET_SHORTCUTS' }),
  ]);

  // Success path — extension supports the v0.5.6 surface.
  // Note we discriminate on the PRESENCE of `shortcuts`, not its length —
  // an empty array would (in some weird future state) still be valid data,
  // distinct from a failure response that omits the field entirely.
  if (shortcutsResult.kind === 'ok' && Array.isArray(shortcutsResult.data.shortcuts)) {
    return { kind: 'supported', shortcuts: shortcutsResult.data.shortcuts };
  }

  // Extension responded to GET_SHORTCUTS with an explicit error (e.g.
  // chrome.commands.getAll() threw). Treat as a recoverable error — show
  // the copy-link fallback rather than claiming the extension is old.
  if (shortcutsResult.kind === 'ok' && shortcutsResult.data.error) {
    return { kind: 'error', message: shortcutsResult.data.error };
  }

  // PING gives us the most reliable installation signal.
  const extensionPresent = pingResult.kind === 'ok' || pingResult.kind === 'no_handler';
  if (!extensionPresent) {
    return { kind: 'not_installed' };
  }

  // Extension IS installed. Now distinguish between:
  //   - 'no_handler' on GET_SHORTCUTS  → listener present but doesn't know
  //                                       this message type. That's old.
  //   - 'error' on GET_SHORTCUTS       → transport failed (timeout, etc.).
  //                                       NOT necessarily old — could be a
  //                                       v0.5.6 extension whose worker is
  //                                       slow/asleep. Surface as error so
  //                                       the user gets "try again" + the
  //                                       copy-link fallback rather than a
  //                                       misleading "please update."
  if (shortcutsResult.kind === 'no_handler') {
    return { kind: 'installed_old' };
  }
  if (shortcutsResult.kind === 'error') {
    return { kind: 'error', message: shortcutsResult.message };
  }
  // shortcutsResult.kind === 'not_installed' would be odd here (PING worked,
  // GET_SHORTCUTS got "Receiving end does not exist"?) — treat as old to be
  // safe, since the user clearly has the listener reachable somewhere.
  return { kind: 'installed_old' };
}

export type AggregateShortcutsState =
  | { state: 'supported'; shortcuts: ShortcutInfo[] }
  | { state: 'installed_old' }
  | { state: 'not_installed' };

/**
 * Aggregate across all configured extension IDs. Precedence:
 *
 *   supported  >  installed_old  >  not_installed
 *
 * Rationale: if BOTH a prod (v0.5.6) and a dev (v0.5.5) variant are
 * installed simultaneously (common during local dev), we want the user to
 * see the v0.5.6 data — not a misleading "please update" prompt. Errors
 * are dropped from the aggregate as long as at least one ID succeeded;
 * if EVERY ID errors, we surface it.
 */
export async function probeShortcuts(): Promise<AggregateShortcutsState | { state: 'error'; message: string }> {
  const ids = getExtensionIds();
  if (ids.length === 0) return { state: 'not_installed' };
  const results = await Promise.all(ids.map(probeShortcutsForId));

  const supported = results.find((r): r is Extract<ShortcutsProbeResult, { kind: 'supported' }> => r.kind === 'supported');
  if (supported) return { state: 'supported', shortcuts: supported.shortcuts };

  if (results.some((r) => r.kind === 'installed_old')) {
    return { state: 'installed_old' };
  }

  const errorResult = results.find((r): r is Extract<ShortcutsProbeResult, { kind: 'error' }> => r.kind === 'error');
  if (errorResult && results.every((r) => r.kind === 'error' || r.kind === 'not_installed')) {
    return { state: 'error', message: errorResult.message };
  }

  return { state: 'not_installed' };
}

// ────────────────────────────────────────────────────────────────────
// Open chrome://extensions/shortcuts (v0.5.6+)
// ────────────────────────────────────────────────────────────────────

/**
 * Try installed extension IDs SEQUENTIALLY; resolve `true` on the first ok.
 *
 * Why sequential (and not Promise.all): if prod and a dev variant are BOTH
 * installed and both v0.5.6+, a parallel fan-out opens
 * `chrome://extensions/shortcuts` once per extension — two duplicate tabs
 * for the user. Sequential + stop-on-first-success keeps it to one tab in
 * the common case while still trying every ID when the first one is unreachable.
 *
 * The UI keeps CopyShortcutsLink visible regardless, so a `false` here
 * just means the user has to copy/paste the URL.
 */
export async function openShortcutsPageViaExtension(): Promise<boolean> {
  const ids = getExtensionIds();
  for (const id of ids) {
    const r = await sendToExtension<{ ok?: boolean }>(id, { type: 'OPEN_SHORTCUTS_PAGE' });
    if (r.kind === 'ok' && r.data.ok === true) {
      return true;
    }
  }
  return false;
}
